import { Box, IconButton, Menu, MenuItem, styled } from '@mui/material';
import TextSearch from '../../../controls/TextSearch';
import SearchIcon from '@mui/icons-material/Search';
import { useState } from 'react';
import { useCommonTranslation } from '../../../../hooks/useCommonTranslation';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import {
  InventoryServiceConfiguration,
  PackageOfferingTimes,
} from '../../../../data/models/inventory-service-configuration.model';
import { DataGridPro } from '../../../tables/DataGridPro';
import { DateTime } from 'luxon';
import { hasWordStartingWith } from '../../../../utils/string.utils';
import { MoreVert } from '@mui/icons-material';
import { useIsSuperReaderUser } from '../../../../hooks/permissions/useIsSuperReaderUser';
import { InventoryManagementDialog } from './InventoryManagementDialog';

const PREFIX = 'InventoryManagementProperties';

const classes = {
  search: `${PREFIX}-search`,
  moreButtonContainer: `${PREFIX}-moreButtonContainer`,
  moreButton: `${PREFIX}-moreButton`,
  hoveredRow: `${PREFIX}-hoveredRow`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),

  [`& .${classes.search}`]: {
    width: 327,
  },
  [`& .${classes.moreButtonContainer}`]: {
    display: 'flex',
    justifyContent: 'end',
    width: '100%',
  },
  [`& .${classes.moreButton}`]: {
    marginRight: theme.spacing(1),
    svg: {
      width: 20,
      height: 20,
    },
  },
}));

const StyledDataGridPro = styled(DataGridPro)(({ theme }) => ({
  cursor: 'pointer',
  [`& .MuiDataGrid-cell`]: {
    paddingLeft: 10,
  },
  [`& .${classes.hoveredRow}`]: {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    minWidth: '164px',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  ...theme.typography.body2,
  height: 52,
}));

export interface InventoryManagementPropertiesProps {
  serviceConfiguration?: InventoryServiceConfiguration;
  handleViewActivity: () => void;
}

interface InventoryManagementProperty {
  id: keyof InventoryServiceConfiguration;
  name: string;
  description: string;
  displayValue: string;
}

interface InventoryManagementDialogProperty {
  id: keyof InventoryServiceConfiguration;
  title: string;
  description: string;
  isReadOnly: boolean;
}

export function InventoryManagementProperties({
  serviceConfiguration,
  handleViewActivity,
}: InventoryManagementPropertiesProps) {
  const t = useCommonTranslation();
  const isSuperReaderUser = useIsSuperReaderUser();
  const [searchText, setSearchText] = useState('');
  const [hoveredRow, setHoveredRow] = useState<string | undefined>(undefined);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuOpenedRow, setMenuOpenedRow] = useState<string | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<
    InventoryManagementProperty | undefined
  >(undefined);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogProperties, setDialogProperties] =
    useState<InventoryManagementDialogProperty | null>(null);

  const formatPackageOfferTime = (
    packageOfferingTimes?: PackageOfferingTimes,
  ) => {
    if (!packageOfferingTimes) {
      return '';
    }
    const formattedTimes = packageOfferingTimes.times.map((time, index) => {
      const dt = DateTime.fromFormat(time, 'HH:mm:ss');
      const formatted = dt.toFormat('h:mma');
      return `${t('inventoryManagement.offer')} ${index + 1}: ${formatted}`;
    });
    return formattedTimes.join(', ');
  };

  const gridItems: InventoryManagementProperty[] = [
    {
      id: 'tripLegNearDueThresholdDays',
      name: t('inventoryManagement.daysBeforeDueTitle'),
      description: t('inventoryManagement.daysBeforeDueDescription'),
      displayValue: `${t('inventoryManagement.days')}: ${serviceConfiguration?.tripLegNearDueThresholdDays}`,
    },
    {
      id: 'packageOfferingTimes',
      name: t('inventoryManagement.packageOfferTimeTitle'),
      description: t('inventoryManagement.packageOfferTimeDescription'),
      displayValue: formatPackageOfferTime(
        serviceConfiguration?.packageOfferingTimes,
      ),
    },
    {
      id: 'targetPackageSize',
      name: t('inventoryManagement.targetPackageSizeTitle'),
      description: t('inventoryManagement.targetPackageSizeDescription'),
      displayValue: `${t('inventoryManagement.providerCapacity')}: ${(serviceConfiguration?.targetPackageSize.percentCapacitySize ?? 0) * 100}%`,
    },
    {
      id: 'packageOfferingTimeoutMinutes',
      name: t('inventoryManagement.timeToAcceptTitle'),
      description: t('inventoryManagement.timeToAcceptDescription'),
      displayValue: `${t('inventoryManagement.minutes')}: ${serviceConfiguration?.packageOfferingTimeoutMinutes}`,
    },
    {
      id: 'tripLegValuationDistanceThreshold',
      name: t('inventoryManagement.tripLegValueDistanceTitle'),
      description: t('inventoryManagement.tripLegValueDistanceDescription'),
      displayValue: `${t('inventoryManagement.miles')}: ${serviceConfiguration?.tripLegValuationDistanceThreshold}`,
    },
    {
      id: 'weightedRandomization',
      name: t('inventoryManagement.weightedRandomizationTitle'),
      description: t('inventoryManagement.weightedRandomizationDescription'),
      displayValue: `W = ${serviceConfiguration?.weightedRandomization.priorityFactorCoefficient}p + ${serviceConfiguration?.weightedRandomization.scoreFactorCoefficient}s + ${serviceConfiguration?.weightedRandomization.capacityFactorCoefficient}c`,
    },
  ];

  const filteredGridItems = gridItems.filter((gridItem) =>
    hasWordStartingWith(gridItem.name?.toLowerCase(), searchText),
  );

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('inventoryManagement.name'),
      sortable: false,
      minWidth: 400,
    },
    {
      field: 'displayValue',
      headerName: t('inventoryManagement.value'),
      sortable: false,
      minWidth: 350,
    },
    {
      field: 'action',
      headerName: '',
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        if (params.row.id === hoveredRow) {
          return (
            <Box className={classes.moreButtonContainer}>
              <IconButton
                className={classes.moreButton}
                onClick={(event) => handleMenuClick(event, params.row.id)}>
                <MoreVert />
              </IconButton>
            </Box>
          );
        }
      },
    },
  ];

  const openDialog = (
    selectedProperty: InventoryManagementProperty,
    isReadOnly = false,
  ) => {
    setDialogProperties({
      id: selectedProperty.id,
      title: selectedProperty?.name ?? '',
      description: selectedProperty?.description ?? '',
      isReadOnly,
    });
    setDialogOpen(true);
  };

  const handleSearchTextChange = (val: string) => {
    setSearchText(val);
  };

  const handleRowClick = (params: GridRowParams) => {
    const property = gridItems.find((item) => item.id === params.id);
    if (property) {
      openDialog(property, isSuperReaderUser);
    }
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    id: string,
  ) => {
    event.stopPropagation();
    setMenuOpenedRow(id);

    setMenuAnchorEl(event.currentTarget);
    const property = gridItems.find((item) => item.id === id);
    setSelectedProperty(property);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setHoveredRow(undefined);
    setMenuOpenedRow(null);
  };

  const handleMenuClose = () => {
    closeMenu();
  };

  const handleViewClick = () => {
    closeMenu();
    if (selectedProperty) {
      openDialog(selectedProperty, true);
    }
  };

  const handleEditClick = () => {
    closeMenu();
    if (selectedProperty) {
      openDialog(selectedProperty);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  return (
    <StyledBox>
      <TextSearch
        id="text-search"
        className={classes.search}
        icon={<SearchIcon />}
        label={t('inventoryManagement.search')}
        currentSearchFilter={searchText}
        displayClearButton={false}
        onSearchChange={handleSearchTextChange}
      />
      <StyledDataGridPro
        disableColumnMenu={true}
        getRowId={(row) => row.id}
        rows={filteredGridItems}
        columns={columns}
        rowCount={filteredGridItems?.length}
        autoHeight
        onRowClick={handleRowClick}
        rowHeight={56}
        pagination={true}
        getRowClassName={(params) =>
          params.row.id === menuOpenedRow ? classes.hoveredRow : ''
        }
        slotProps={{
          row: {
            onMouseEnter: (event) => {
              const id = event.currentTarget.dataset.id;
              setHoveredRow(id);
            },
          },
        }}
      />
      <StyledMenu
        anchorEl={menuAnchorEl}
        open={!!menuAnchorEl}
        onClose={handleMenuClose}
        transformOrigin={{
          vertical: 'top',
          horizontal: 115,
        }}>
        {isSuperReaderUser && (
          <StyledMenuItem onClick={() => handleViewClick()}>
            {t('inventoryManagement.view')}
          </StyledMenuItem>
        )}
        {!isSuperReaderUser && (
          <StyledMenuItem onClick={() => handleEditClick()}>
            {t('inventoryManagement.edit')}
          </StyledMenuItem>
        )}
      </StyledMenu>
      <InventoryManagementDialog
        open={dialogOpen}
        propertyId={dialogProperties?.id ?? null}
        title={dialogProperties?.title ?? ''}
        description={dialogProperties?.description ?? ''}
        serviceConfiguration={serviceConfiguration}
        handleClose={handleDialogClose}
        handleViewActivity={handleViewActivity}
        isReadOnly={isSuperReaderUser}
      />
    </StyledBox>
  );
}
